import React, { useContext, useMemo, useState } from 'react';
import * as yup from 'yup';
import {
  Icon,
  H2,
  H3,
  H4,
  P,
  S,
  TimePicker,
  TimeIntervals,
  TagInput,
  Flex,
  Modal,
  Button,
  IconButton,
} from 'design-system-web';
import {
  Button as ComponentsButton,
  Checkbox,
  Input,
  Loading,
  Thumbnail,
  Tooltip,
} from 'components';
import { useIsAdminView } from 'hooks';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { routes } from 'lane-shared/config';
import { AppContext } from 'lane-shared/contexts';
import { getClient } from 'lane-shared/apollo';
import {
  END_HOUR,
  START_HOUR,
} from 'lane-shared/domains/visitorManagement/features/VisitorManagementFeatureValues';
import { createDefaultDateTime } from 'date-time-manipulations';
import {
  EventStatus,
  VisitorPassQueryResponse,
  VisitorWatchlistMatchDetails,
} from 'lane-shared/domains/visitorManagement/types';
import { parseRRuleSchedule } from 'lane-shared/domains/visitorManagement/helpers/parseRRuleSchedule';
import {
  getRecurrenceVisitorPassDetailsById,
  getVisitorPassDetailsById,
} from 'lane-shared/graphql/visitorManagement';
import { setSubmissionVisitorApproval } from 'graphql-queries';
import { getTimeZoneByGeoLocation } from 'async-timing';
import {
  SHORT_TIME_WITH_TZ,
  LONG_DAY_LONG_DATE,
  SHORT_TIME,
  SIMPLE_DATE,
  SHORT_DATE,
} from 'lane-shared/helpers/constants/dates';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { convertToUUID } from 'uuid-encoding';
import { explodeFeatures } from 'lane-shared/helpers/features';
import {
  dateFormatter,
  addressFormatter,
  imageUrl,
} from 'lane-shared/helpers/formatters';
import { formatIncompletePhoneNumber } from 'lane-shared/helpers/phoneNumbers';
import { useFlag, useContentQuery } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import { ChipSelect } from 'components/ads';
import TextArea from 'components/form/TextArea';
import { AdminPage } from 'components/layout';
import { BreadCrumbs, Alert, AlertType } from 'components/lds';
import {
  WatchlistMatchCards,
  WatchlistMatch,
  ApprovalActionButtons,
  VisitorPassStatusSelect,
} from '../../components';
import type { ConfirmationActionType, ConfirmationModalState } from '../visitor-log/tabs/pending-approval';
import { FONT_AWESOME_REGULAR } from 'constants-activate';
import DatePickerButton from '../../components/DatePickers/DatePickerButton';
import { useEditVisitorPassDetails } from '../../hooks/useEditVisitorPassDetails';
import { useStatusSelector } from '../../hooks/useStatusSelector';
import { StatusListItems } from '../../types/EventStatus';

import styles from './styles.scss';

import { useOrganizationSettingsData } from 'lane-shared/domains/visitorManagement/hooks/useOrganizationSettingsData';

const combineNames = (names: (string | undefined)[]): string => {
  return names.filter(name => Boolean(name)).join(' ');
};

const invalidStatusList = [
  EventStatus.EVENT_STATUS_CANCELLED.toString(),
  EventStatus.EVENT_STATUS_EXPIRED.toString(),
  EventStatus.EVENT_STATUS_PENDING.toString(),
  EventStatus.EVENT_STATUS_ARCHIVED.toString(),
  EventStatus.EVENT_STATUS_INVALID.toString(),
];

type RecurrenceData = {
  schedule: string;
  scheduleDuration: string;
  recurrenceId?: string;
};

type QueryResponse = {
  visitorPass: VisitorPassQueryResponse;
  recurrence?: RecurrenceData;
};

const TRANSLATION_KEYS = {
  recurrenceDetails:
    'web.admin.channel.visitor.passDetails.visitDetails.recurrence.details',
  recurrenceSchedule:
    'web.admin.channel.visitor.passDetails.visitDetails.recurrence.schedule',
  recurrenceEditWarning:
    'web.admin.channel.visitor.passDetails.visitDetails.recurrence.edit.warning',
  modalApproveTitle: 'web.admin.channel.visitor.log.pendingApproval.modal.approve.title',
  modalApproveDescription: 'web.admin.channel.visitor.log.pendingApproval.modal.approve.description',
  modalApproveDescriptionRecurring: 'web.admin.channel.visitor.log.pendingApproval.modal.approve.description.recurring',
  modalDenyTitle: 'web.admin.channel.visitor.log.pendingApproval.modal.deny.title',
  modalDenyDescription: 'web.admin.channel.visitor.log.pendingApproval.modal.deny.description',
  modalDenyDescriptionRecurring: 'web.admin.channel.visitor.log.pendingApproval.modal.deny.description.recurring',
  modalReturnToPage: 'web.admin.channel.visitor.log.pendingApproval.modal.button.returnToPage',
  actionApprove: 'web.admin.channel.visitor.log.pendingApproval.action.approve',
  actionDeny: 'web.admin.channel.visitor.log.pendingApproval.action.deny',
};

const initialModalState: ConfirmationModalState = {
  isOpen: false,
  actionType: null,
  submissionVisitorId: '',
  visitorName: '',
  isRecurring: false,
};

export function VisitorPassDetailsPage({ channel }: { channel: any }) {
  const { t } = useTranslation();
  const { whitelabel } = useContext(AppContext);
  const [, channelSlug] = useIsAdminView();
  const timezone = getTimeZoneByGeoLocation({
    latitude: channel.address.geo[1],
    longitude: channel.address.geo[0],
  });

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isApprovalLoading, setIsApprovalLoading] = useState(false);
  const [modalState, setModalState] = useState<ConfirmationModalState>(initialModalState);
  const { visitorPassId } = useParams<{ visitorPassId: string }>();
  const passUUID = convertToUUID(visitorPassId);
  const visitorNotesEnabled = useFlag(
    FeatureFlag.VisitorManagementVisitorNotes,
    false
  );
  const multipleNotificationRecipientsEnabled = useFlag(
    FeatureFlag.VisitorManagementMultipleNotificationRecipients,
    false
  );
  const history = useHistory();

  const { data, loading, error, refetch } = useQuery<QueryResponse>(
    getVisitorPassDetailsById,
    {
      variables: {
        id: passUUID,
        channelId: channel._id,
      },
      fetchPolicy: 'network-only',
    }
  );

  const {
    content,
    loading: isContentLoading,
    error: contentError,
  } = useContentQuery({
    contentId: data?.visitorPass?.sourceId,
  });

  const { loadingStates, updateStatus } = useStatusSelector();

  const {
    isLoading,
    startTime,
    endTime,
    isAllDaySelected,
    isAllDaySelectable,
    isDateEditable,
    isStartTimeEditable,
    isEndTimeEditable,
    isPristine,
    staffNote,
    visitorNote,
    updateDate,
    updateStartTime,
    updateEndTime,
    toggleAllDaySelection,
    updateVisitorNote,
    updateStaffNote,
    reset,
    onSave,
    additionalRecipients,
    updateAdditionalRecipients,
    handleAdditionalRecipientsErrorChange,
  } = useEditVisitorPassDetails({ pass: data?.visitorPass, timezone });

  const { organizationSettings } = useOrganizationSettingsData(
    convertToUUID(channel?._id)
  );

  const maxDate = useMemo(() => {
    let date;

    if (content?.features) {
      const result = explodeFeatures(content?.features);

      if (result.visitorManagementFeature) {
        date = new Date();
        date.setMonth(
          date.getMonth() +
            result.visitorManagementFeature?.scheduleInAdvanceAmount
        );
      }
    }

    return date;
  }, [content?.features]);

  const isAllDayPassEnabled = useMemo(() => {
    if (content?.features) {
      const result = explodeFeatures(content?.features);

      return result.visitorManagementFeature?.allDayPassEnabled || false;
    }

    return false;
  }, [content?.features]);

  const shouldRender = useMemo(() => {
    return (
      !loading &&
      !isContentLoading &&
      !error &&
      !contentError &&
      Boolean(data?.visitorPass)
    );
  }, [loading, isContentLoading, error, contentError, data]);

  const shouldDisplayNotFound = useMemo(() => {
    return (
      !loading &&
      !isContentLoading &&
      !error &&
      !contentError &&
      !data?.visitorPass
    );
  }, [loading, isContentLoading, error, contentError, data]);

  const isPassEditable = useMemo(() => {
    return data?.visitorPass
      ? !invalidStatusList.includes(data.visitorPass.status) &&
          Boolean(data.visitorPass.validStateTransitions.length)
      : false;
  }, [data?.visitorPass]);

  // Fetch recurrence visitor pass details if a recurrenceId is present
  const recurrenceId = data?.visitorPass?.recurrenceId;
  const { data: recurrenceData, loading: recurrenceLoading } =
    useQuery<QueryResponse>(getRecurrenceVisitorPassDetailsById, {
      skip: !recurrenceId,
      variables: { id: recurrenceId },
      fetchPolicy: 'network-only',
    });

  const isRecurrenceVisit = useMemo(() => {
    return (
      data?.visitorPass?.recurrenceId &&
      recurrenceData?.recurrence?.recurrenceId
    );
  }, [data?.visitorPass, recurrenceData]);

  // Transform watchlist details to WatchlistMatchCards format
  const watchlistMatches: WatchlistMatch[] = useMemo(() => {
    const matches = data?.visitorPass?.watchlistDetails?.matches;

    if (!matches || matches.length === 0) {
      return [];
    }

    return matches
      .filter((match: VisitorWatchlistMatchDetails) => match.visitorWatchlist)
      .map((match: VisitorWatchlistMatchDetails) => ({
        id: match.visitorWatchlist!.id,
        organizationId: match.visitorWatchlist!.organizationId,
        visitorMatchingInfo: {
          firstName: match.visitorWatchlist!.visitorMatchingInfo?.firstName ?? null,
          lastName: match.visitorWatchlist!.visitorMatchingInfo?.lastName ?? null,
          email: match.visitorWatchlist!.visitorMatchingInfo?.email ?? null,
          phone: match.visitorWatchlist!.visitorMatchingInfo?.phone ?? null,
        },
        severityLevel: match.visitorWatchlist!.severityLevel,
        notes: match.notes ?? match.visitorWatchlist!.notes ?? '',
        attachmentIds: match.visitorWatchlist!.attachmentIds ?? [],
        reportedBy: match.visitorWatchlist!.createdByName ?? undefined,
      }));
  }, [data?.visitorPass?.watchlistDetails?.matches]);

  const hasWatchlistMatches = watchlistMatches.length > 0;

  // Show approval buttons for watchlisted visitors (except rejected and upcoming ones)
  const showApprovalButtons = useMemo(() => {
    const isRejected =
      data?.visitorPass?.status === EventStatus.EVENT_STATUS_REJECTED.toString();
    const isUpcoming =
      data?.visitorPass?.status === EventStatus.EVENT_STATUS_UPCOMING.toString();
    const hasApprovalRequired = data?.visitorPass?.visitorWatchlistMatchInfo?.some(
      match => match.isApprovalRequired
    ) ?? false;

    return hasApprovalRequired && !isRejected && !isUpcoming;
  }, [data?.visitorPass?.status, data?.visitorPass?.visitorWatchlistMatchInfo]);

  // Get submissionVisitorId from visitorPass
  const submissionVisitorId = useMemo(() => {
    return data?.visitorPass?.submissionVisitorId;
  }, [data?.visitorPass?.submissionVisitorId]);

  const handleUpdateStatus = async (
    status: string,
    pass: VisitorPassQueryResponse
  ): Promise<void> => {
    if (EventStatus.EVENT_STATUS_CANCELLED.toString() === status) {
      setIsCancelModalOpen(true);

      return;
    }

    try {
      await updateStatus(status, pass);
      await refetch();
    } catch (err) {
      const error = err as Error;

      window.Toast.show(error.message, 5000);
    }
  };

  const handleModalCancelOnClick = async () => {
    if (!shouldRender) return;

    try {
      await updateStatus(
        EventStatus.EVENT_STATUS_CANCELLED.toString(),
        data!.visitorPass
      );
      await refetch();
      const message = t(
        'web.admin.channel.visitor.passDetails.cancelation.success'
      );

      window.Toast.show(message, 5000);
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      const message = t(
        'web.admin.channel.visitor.passDetails.cancelation.failure'
      );

      window.Toast.show(message, 5000);
    }

    setIsCancelModalOpen(false);
  };

  const openConfirmationModal = (
    actionType: ConfirmationActionType,
    submissionVisitorId: string,
    visitorName: string,
    isRecurring: boolean
  ) => {
    setModalState({
      isOpen: true,
      actionType,
      submissionVisitorId,
      visitorName,
      isRecurring,
    });
  };

  const closeConfirmationModal = () => {
    setModalState(initialModalState);
  };

  const handleApprove = () => {
    if (!shouldRender || !submissionVisitorId) {
      if (!submissionVisitorId) {
        console.error('submissionVisitorId is missing', {
          submissionVisitorId: data?.visitorPass?.submissionVisitorId,
          visitorPassId: data?.visitorPass?.id,
          submissionId: data?.visitorPass?.submissionId,
          visitorId: data?.visitorPass?.visitor?.id,
        });
        const message = t('web.admin.channel.visitor.passDetails.approval.failure');

        window.Toast.show(message, 5000);
      }

      return;
    }

    const visitorName = combineNames([
      data?.visitorPass.visitor.firstName,
      data?.visitorPass.visitor.lastName,
    ]);

    openConfirmationModal('approve', submissionVisitorId as string, visitorName, Boolean(isRecurrenceVisit));
  };

  const handleDeny = () => {
    if (!shouldRender || !submissionVisitorId) {
      if (!submissionVisitorId) {
        console.error('submissionVisitorId is missing', {
          submissionVisitorId: data?.visitorPass?.submissionVisitorId,
          visitorPassId: data?.visitorPass?.id,
          submissionId: data?.visitorPass?.submissionId,
          visitorId: data?.visitorPass?.visitor?.id,
        });
        const message = t('web.admin.channel.visitor.passDetails.denial.failure');

        window.Toast.show(message, 5000);
      }

      return;
    }

    const visitorName = combineNames([
      data?.visitorPass.visitor.firstName,
      data?.visitorPass.visitor.lastName,
    ]);

    openConfirmationModal('deny', submissionVisitorId as string, visitorName, Boolean(isRecurrenceVisit));
  };

  const handleConfirmAction = async () => {
    if (!modalState.actionType || !shouldRender) return;

    if (!modalState.submissionVisitorId) {
      console.error('submissionVisitorId is missing', {
        submissionVisitorId: data?.visitorPass?.submissionVisitorId,
        visitorPassId: data?.visitorPass?.id,
        submissionId: data?.visitorPass?.submissionId,
        visitorId: data?.visitorPass?.visitor?.id,
      });
      const errorMessage = modalState.actionType === 'approve'
        ? t('web.admin.channel.visitor.passDetails.approval.failure')
        : t('web.admin.channel.visitor.passDetails.denial.failure');

      window.Toast.show(errorMessage, 5000);
      closeConfirmationModal();

      return;
    }

    setIsApprovalLoading(true);

    try {
      const submissionVisitorIdUUID = convertToUUID(modalState.submissionVisitorId);
      const approvalStatus = modalState.actionType === 'approve'
        ? 'APPROVAL_STATUS_APPROVED'
        : 'APPROVAL_STATUS_REJECTED';

      await getClient().mutate({
        mutation: setSubmissionVisitorApproval,
        variables: {
          input: {
            submissionVisitorId: submissionVisitorIdUUID,
            approvalStatus,
            organizationId: channel._id,
          },
        },
      });
      await refetch();
      
      const successMessage = modalState.actionType === 'approve'
        ? t('web.admin.channel.visitor.passDetails.approval.success', { visitorName: modalState.visitorName })
        : t('web.admin.channel.visitor.passDetails.denial.success', { visitorName: modalState.visitorName });

      window.Toast.show(successMessage, 5000);
      closeConfirmationModal();
    } catch {
      const errorMessage = modalState.actionType === 'approve'
        ? t('web.admin.channel.visitor.passDetails.approval.failure')
        : t('web.admin.channel.visitor.passDetails.denial.failure');

      window.Toast.show(errorMessage, 5000);
    } finally {
      setIsApprovalLoading(false);
    }
  };

  const getModalTitle = () => {
    if (modalState.actionType === 'approve') {
      return t(TRANSLATION_KEYS.modalApproveTitle);
    }

    return t(TRANSLATION_KEYS.modalDenyTitle);
  };

  const getModalDescription = () => {
    if (modalState.actionType === 'approve') {
      return modalState.isRecurring
        ? TRANSLATION_KEYS.modalApproveDescriptionRecurring
        : TRANSLATION_KEYS.modalApproveDescription;
    }

    return modalState.isRecurring
      ? TRANSLATION_KEYS.modalDenyDescriptionRecurring
      : TRANSLATION_KEYS.modalDenyDescription;
  };

  const getConfirmButtonText = () => {
    if (modalState.actionType === 'approve') {
      return t(TRANSLATION_KEYS.actionApprove);
    }

    return t(TRANSLATION_KEYS.actionDeny);
  };


  const validateOnEditSave = (): string => {
    const startTimeMillis = DateTime.fromISO(
      startTime ?? new Date().toISOString()
    ).toMillis();
    const endTimeMillis = DateTime.fromISO(
      endTime ?? new Date().toISOString()
    ).toMillis();
    const currentTime = new Date().getTime();

    if (isEndTimeEditable && currentTime >= endTimeMillis) {
      return t(
        'web.admin.channel.visitor.passDetails.validationError.currentTimeEndTime'
      );
    }

    if (startTimeMillis >= endTimeMillis) {
      return t(
        'web.admin.channel.visitor.passDetails.validationError.startTimeEndTime'
      );
    }

    return '';
  };

  const emailValidator = yup.string().email().nullable();
  const validateEmail = (email: string) => {
    try {
      emailValidator.validateSync(email);

      return true;
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      return false;
    }
  };

  const onEditCancel = () => {
    setIsEditMode(false);
    reset();
  };

  const onEditSave = async () => {
    const validationErrorMessage = validateOnEditSave();

    if (validationErrorMessage) {
      window.Toast.show(validationErrorMessage, 5000);

      return;
    }

    try {
      await onSave();

      const visitorName = combineNames([
        data?.visitorPass.visitor.firstName,
        data?.visitorPass.visitor.lastName,
      ]);
      const message = t(
        'web.admin.channel.visitor.passDetails.update.success',
        {
          visitorName,
        }
      );

      window.Toast.show(message, 5000);
      setIsEditMode(false);
      refetch();
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      const message = t('web.admin.channel.visitor.passDetails.update.failure');

      window.Toast.show(message, 5000);
    }
  };

  const getTitle = (): string => {
    const visitorName = combineNames([
      data?.visitorPass.visitor.firstName,
      data?.visitorPass.visitor.lastName,
    ]);

    return t('web.admin.channel.visitor.passDetails.title', {
      visitorName,
    });
  };

  const getCreatedOnInfo = (): string => {
    const createdDate = data!.visitorPass.createdAtDatetime
      ? dateFormatter(
          data!.visitorPass.createdAtDatetime,
          LONG_DAY_LONG_DATE,
          timezone
        )
      : '-';
    const createdTime = data!.visitorPass.createdAtDatetime
      ? dateFormatter(
          data!.visitorPass.createdAtDatetime,
          SHORT_TIME_WITH_TZ,
          timezone
        )
      : '-';
    const createdBy = data!.visitorPass.submittedBy;

    return t('web.admin.channel.visitor.passDetails.createdOn', {
      createdDate,
      createdTime,
      createdBy,
    });
  };

  const getUpdatedOnInfo = (): string => {
    const updatedDate = data!.visitorPass.updatedAtDatetime
      ? dateFormatter(
          data!.visitorPass.updatedAtDatetime,
          LONG_DAY_LONG_DATE,
          timezone
        )
      : '-';
    const updatedTime = data!.visitorPass.updatedAtDatetime
      ? dateFormatter(
          data!.visitorPass.updatedAtDatetime,
          SHORT_TIME_WITH_TZ,
          timezone
        )
      : '-';
    const updatedBy = data!.visitorPass.updatedBy;

    if (!updatedBy) {
      // means the most recent update was automated
      return t('web.admin.channel.visitor.passDetails.automatedUpdate', {
        updatedDate,
        updatedTime,
        whiteLabelName: whitelabel.name,
      });
    }

    const createdDate = data!.visitorPass.createdAtDatetime
      ? dateFormatter(
          data!.visitorPass.createdAtDatetime,
          LONG_DAY_LONG_DATE,
          timezone
        )
      : '-';
    const createdTime = data!.visitorPass.createdAtDatetime
      ? dateFormatter(
          data!.visitorPass.createdAtDatetime,
          SHORT_TIME_WITH_TZ,
          timezone
        )
      : '-';
    const createdBy = data!.visitorPass.submittedBy;

    if (
      updatedDate === createdDate &&
      updatedTime === createdTime &&
      updatedBy === createdBy
    ) {
      return '';
    }

    return t('web.admin.channel.visitor.passDetails.updatedOn', {
      updatedDate,
      updatedTime,
      updatedBy,
    });
  };

  const getPassDate = (dateTime: string | undefined): string => {
    return dateTime
      ? dateFormatter(dateTime, LONG_DAY_LONG_DATE, timezone)
      : '-';
  };

  const getTimeWindow = (
    startDatetime: string | undefined,
    endDatetime: string | undefined
  ): string => {
    const startTime = startDatetime
      ? dateFormatter(startDatetime, SHORT_TIME, timezone)
      : '-';
    const endTime = endDatetime
      ? dateFormatter(endDatetime, SHORT_TIME_WITH_TZ, timezone)
      : '-';

    return `${startTime} - ${endTime}`;
  };

  const getAllDayTimeWindow = (): string => {
    const allDayStartTime = createDefaultDateTime(
      START_HOUR,
      0,
      timezone
    ).toISOString();
    const allDayEndTime = createDefaultDateTime(
      END_HOUR,
      0,
      timezone
    ).toISOString();

    return getTimeWindow(allDayStartTime, allDayEndTime);
  };

  const getHostEmailAndPhone = (): string => {
    const emailAndPhoneList: string[] = [];
    const joinPattern = ' | ';

    if (data?.visitorPass.host?.profile.email)
      emailAndPhoneList.push(data.visitorPass.host.profile.email);

    if (data?.visitorPass.host?.profile.phone)
      emailAndPhoneList.push(
        formatIncompletePhoneNumber(data.visitorPass.host.profile.phone)
      );

    return emailAndPhoneList.length ? emailAndPhoneList.join(joinPattern) : '';
  };

  const renderStatus = (): React.ReactElement | null => {
    const statusListItem = StatusListItems.find(
      item => item.value === data!.visitorPass.status
    );

    if (!statusListItem) {
      return null;
    }

    if (isEditMode || !isPassEditable) {
      return (
        <ChipSelect.NonInteractive
          value={statusListItem.label}
          type={statusListItem.type}
        />
      );
    }

    return (
      <VisitorPassStatusSelect
        visitorPass={data!.visitorPass}
        loading={loadingStates[data!.visitorPass.id]}
        updateStatus={handleUpdateStatus}
        organizationSettings={organizationSettings}
      />
    );
  };

  const ReturnButton = (
    <ComponentsButton
      key="1"
      onClick={() => setIsCancelModalOpen(false)}
      fullWidth
      testId="returnToPageButton"
    >
      {t('web.admin.channel.visitor.passDetails.cancelModal.returnButton')}
    </ComponentsButton>
  );

  const CancelButton = (
    <ComponentsButton
      key="2"
      variant="contained"
      onClick={handleModalCancelOnClick}
      fullWidth
      testId="cancelPassButton"
      loading={loadingStates[data?.visitorPass?.id || '']}
    >
      {t('web.admin.channel.visitor.passDetails.cancelModal.cancelButton')}
    </ComponentsButton>
  );

  const getDates = (schedule: string, scheduleDuration: string) => {
    const { scheduleText, scheduleStartDatetime, scheduleEndDate } =
      parseRRuleSchedule(schedule, scheduleDuration);

    const startDateFormatted = scheduleStartDatetime
      ? dateFormatter(scheduleStartDatetime, SHORT_DATE, timezone)
      : '';

    const endDateFormatted = scheduleEndDate
      ? dateFormatter(scheduleEndDate, SIMPLE_DATE, timezone)
      : '';

    const scheduleString = `${scheduleText} ${startDateFormatted} to ${endDateFormatted}`;

    return scheduleString;
  };

  const hostFloor = data?.visitorPass?.floor;

  const shouldDisplayWatchlistMatchCards = useMemo(() => {
    return data?.visitorPass?.visitorWatchlistMatchInfo?.some(match => match.isWatchlistDisplayed) ?? false;
  }, [data?.visitorPass?.visitorWatchlistMatchInfo]);


  return (
    <AdminPage>
      <BreadCrumbs
        links={[
          {
            label: t(
              'web.admin.channel.visitor.passDetails.breadcrumb.visitorLog'
            ),
            url: routes.visitorManagementVisitorLog.replace(
              ':id',
              channelSlug || ''
            ),
          },
          {
            label: t(
              'web.admin.channel.visitor.passDetails.breadcrumb.passDetails'
            ),
          },
        ]}
      />
      {(loading || isContentLoading) && <Loading />}
      {(error || contentError) && (
        <Alert type={AlertType.error} fullWidth>
          {t('web.admin.channel.visitor.passDetails.error')}
        </Alert>
      )}
      {shouldDisplayNotFound && (
        <Alert type={AlertType.error} fullWidth>
          {t('web.admin.channel.visitor.passDetails.notFound')}
        </Alert>
      )}
      {shouldRender && (
        <>
          <Modal
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            actions={[ReturnButton, CancelButton]}
            testId=""
            className={styles.modal}
          >
            <H3 mb={4}>
              {t('web.admin.channel.visitor.passDetails.cancelModal.title')}
            </H3>
            <S className={styles.text} mb={4}>
              {t('web.admin.channel.visitor.passDetails.cancelModal.message')}
            </S>
            <S className={styles.text} mb={4}>
              {t('web.admin.channel.visitor.passDetails.cancelModal.warning')}
            </S>
          </Modal>
          <Modal
            isOpen={modalState.isOpen}
            onClose={closeConfirmationModal}
            testId="approvalConfirmationModal"
            className={styles.modal}
            includeHeader={false}
          >
            <Flex justify="space-between" mb={5}>
              <H4 mt={2}>{getModalTitle()}</H4>
              <IconButton
                type={FONT_AWESOME_REGULAR}
                onClick={closeConfirmationModal}
                icon="times"
                data-test="closeButton"
                size="medium"
              />
            </Flex>
            <Flex direction="column" gap={5}>
              <S variant="secondary" mb={5}>
                <Trans i18nKey={getModalDescription()} components={{ strong: <strong /> }} />
              </S>
              <Flex gap={4}>
                <Button
                  variant="primary"
                  onClick={handleConfirmAction}
                  testId="confirm-button"
                  size="large"
                  loading={isApprovalLoading}
                >
                  {getConfirmButtonText()}
                </Button>
                <Button
                  variant="secondary"
                  onClick={closeConfirmationModal}
                  testId="cancel-button"
                  size="large"
                >
                  {t(TRANSLATION_KEYS.modalReturnToPage)}
                </Button>
              </Flex>
            </Flex>
          </Modal>
          <div className={styles.passDetailsPageContainer}>
            <div className={styles.title}>
              <H2>{getTitle()}</H2>
              <S className={styles.info}>{getCreatedOnInfo()}</S>
              {data?.visitorPass.createdAtDatetime !==
                data?.visitorPass.updatedAtDatetime && (
                <S className={styles.info}>{getUpdatedOnInfo()}</S>
              )}
            </div>
            <div className={styles.status}>
              <span className={styles.statusHeader}>
                {t('web.admin.channel.visitor.passDetails.status.title')}
              </span>
              {renderStatus() || '-'}
            </div>
            <div className={styles.contentLayout}>
              <div className={styles.mainContent}>
                <div className={styles.host}>
              <H4>{t('web.admin.channel.visitor.passDetails.host.title')}</H4>
              <div className={styles.itemContainer}>
                <div className={styles.item}>
                  <Thumbnail
                    className={styles.thumbnail}
                    name={data?.visitorPass.host?.name || '-'}
                    src={imageUrl(data?.visitorPass.host?.profile.image)}
                  />
                  <div className={styles.textWrapper}>
                    <P>{data?.visitorPass.host?.name || '-'}</P>
                    <S>{getHostEmailAndPhone()}</S>
                  </div>
                </div>
                <div className={styles.item}>
                  <Thumbnail
                    className={styles.thumbnail}
                    name={data?.visitorPass.tenantName || '-'}
                    src={imageUrl(
                      data?.visitorPass.tenant?.profile?.logo ||
                        data?.visitorPass.tenant?.profile?.image
                    )}
                  />
                  <div className={styles.textWrapper}>
                    <P>{data?.visitorPass.tenantName || '-'}</P>
                    <S>
                      {addressFormatter(data?.visitorPass.tenant?.address, [
                        'name',
                        'street1',
                        'street2',
                        'street3',
                        'city',
                        'state',
                      ])}
                    </S>
                    {hostFloor && (
                      <S>
                        {t(
                          'web.content.feature.visitorManagement.receipt.hostInformation.floor',
                          {
                            floor: hostFloor,
                          }
                        )}
                      </S>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.details}>
              <div className={styles.detailsTitleContainer}>
                <H4>
                  {t(
                    'web.admin.channel.visitor.passDetails.visitDetails.title'
                  )}
                </H4>
                {!isEditMode && isPassEditable && (
                  <ComponentsButton
                    onClick={() => setIsEditMode(true)}
                    testId="editButton"
                    size="small"
                    startIcon={<Icon name="pen" set="FontAwesome" type="far" />}
                  >
                    {t('Edit')}
                  </ComponentsButton>
                )}
              </div>
              <div>
                {isEditMode && isPassEditable && isRecurrenceVisit && (
                  <Alert type={AlertType.warning}>
                    {t(TRANSLATION_KEYS.recurrenceEditWarning)}
                  </Alert>
                )}
              </div>

              {isRecurrenceVisit && (
                <div className={styles.recurrenceScheduleContainer}>
                  <div className={styles.recurrenceScheduleRow}>
                    <div className={styles.scheduleText}>
                      {t(TRANSLATION_KEYS.recurrenceSchedule)}
                    </div>
                  </div>
                  <div className={styles.recurrenceScheduleRow}>
                    <div className={styles.icon}>
                      <Icon
                        name="repeat"
                        set={ICON_SET_FONTAWESOME}
                        type="far"
                      />
                    </div>
                    <div className={styles.dateText}>
                      {recurrenceLoading ? (
                        <Loading />
                      ) : (
                        <>
                          {recurrenceData?.recurrence?.schedule &&
                            recurrenceData?.recurrence?.scheduleDuration &&
                            getDates(
                              recurrenceData?.recurrence?.schedule,
                              recurrenceData?.recurrence?.scheduleDuration
                            )}
                        </>
                      )}
                    </div>
                  </div>
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                  <div
                    className={styles.viewRecurrenceDetails}
                    onClick={() =>
                      history.push({
                        pathname: `/l/channel/${channel?.slug}/admin/visitor-management/recurrencedetail/${data?.visitorPass?.recurrenceId}`,
                        state: { data },
                      })
                    }
                  >
                    {t(TRANSLATION_KEYS.recurrenceDetails)}
                  </div>
                </div>
              )}

              <div className={styles.rowsContainer}>
                <div className={styles.row}>
                  <div className={styles.rowItem}>
                    <S mb={1}>
                      {t(
                        'web.admin.channel.visitor.passDetails.visitDetails.visitorName'
                      )}
                    </S>
                    <P>
                      {combineNames([
                        data?.visitorPass.visitor.firstName,
                        data?.visitorPass.visitor.lastName,
                      ])}
                    </P>
                  </div>
                  <div className={styles.rowItem}>
                    <S mb={1}>
                      {t(
                        'web.admin.channel.visitor.passDetails.visitDetails.visitorEmail'
                      )}
                    </S>
                    <P>{data?.visitorPass.visitor.email || '-'}</P>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.rowItem}>
                    <S mb={1}>
                      {t(
                        'web.admin.channel.visitor.passDetails.visitDetails.visitorCompany'
                      )}
                    </S>
                    <P>{data?.visitorPass.visitor.company || '-'}</P>
                  </div>
                  <div className={styles.rowItem}>
                    <S mb={1}>
                      {t(
                        'web.admin.channel.visitor.passDetails.visitDetails.visitorPhoneNumber'
                      )}
                    </S>
                    <P>
                      {data?.visitorPass.visitor.phone
                        ? formatIncompletePhoneNumber(
                            data.visitorPass.visitor.phone
                          )
                        : '-'}
                    </P>
                  </div>
                </div>
                {!isEditMode && (
                  <div className={styles.row}>
                    <div className={styles.rowItem}>
                      <S mb={1}>
                        {t(
                          'web.admin.channel.visitor.passDetails.visitDetails.date'
                        )}
                      </S>
                      <P>{getPassDate(startTime)}</P>
                    </div>
                    <div className={styles.rowItem}>
                      <S mb={1}>
                        {t(
                          'web.admin.channel.visitor.passDetails.visitDetails.timeWindow'
                        )}
                      </S>
                      <P>{getTimeWindow(startTime, endTime)}</P>
                    </div>
                  </div>
                )}
                {!isEditMode && visitorNotesEnabled && (
                  <div className={styles.row}>
                    <div className={styles.rowItem}>
                      <S mb={1}>
                        {t(
                          'web.admin.channel.visitor.passDetails.visitDetails.noteToStaff'
                        )}
                      </S>
                      <P className={styles.notes}>
                        {data?.visitorPass?.staffNote || '-'}
                      </P>
                    </div>
                    <div className={styles.rowItem}>
                      <S mb={1}>
                        {t(
                          'web.admin.channel.visitor.passDetails.visitDetails.noteToVisitor'
                        )}
                      </S>
                      <P className={styles.notes}>
                        {data?.visitorPass?.visitorNote || '-'}
                      </P>
                    </div>
                  </div>
                )}
                {!isEditMode && multipleNotificationRecipientsEnabled && (
                  <div className={styles.row}>
                    <div className={styles.rowItem}>
                      <S mb={1}>
                        {t(
                          'web.admin.channel.visitor.passDetails.visitDetails.additionalRecipients'
                        )}
                      </S>
                      <P className={styles.notes}>
                        {data?.visitorPass?.additionalRecipients?.length
                          ? data?.visitorPass?.additionalRecipients.join(', ')
                          : '-'}
                      </P>
                    </div>
                  </div>
                )}
                {isEditMode && (
                  <>
                    {isAllDayPassEnabled && (
                      <div className={styles.row}>
                        <div className={styles.rowItem}>
                          <div className={styles.allDayLabelContainer}>
                            <S mb={1}>
                              {t(
                                'web.admin.channel.visitor.passDetails.visitDetails.passValidFrom'
                              )}
                            </S>
                            <Tooltip
                              TooltipComponent={t(
                                'web.admin.channel.visitor.passDetails.visitDetails.passValidFrom.tooltip'
                              )}
                              placement="right"
                            >
                              <Icon
                                name="info-circle"
                                className={styles.icon}
                                set={ICON_SET_FONTAWESOME}
                                type="far"
                                size="medium"
                              />
                            </Tooltip>
                          </div>
                          {isAllDaySelectable && (
                            <Checkbox
                              selected={isAllDaySelected}
                              value={isAllDaySelected}
                              onChange={toggleAllDaySelection}
                              className={styles.allDayCheckbox}
                              text={t(
                                'web.admin.channel.visitor.passDetails.visitDetails.allDay',
                                {
                                  allDayTime: getAllDayTimeWindow(),
                                }
                              )}
                              testId="allDayCheckbox"
                            />
                          )}
                          {!isAllDaySelectable && (
                            <Checkbox
                              selected={false}
                              disabled
                              value={false}
                              onChange={() => {}}
                              className={styles.disabledAllDayCheckbox}
                              text={t(
                                'web.admin.channel.visitor.passDetails.visitDetails.allDay',
                                {
                                  allDayTime: getAllDayTimeWindow(),
                                }
                              )}
                              testId="disabledAllDayCheckbox"
                            />
                          )}
                        </div>
                        <div className={styles.rowItem} />
                      </div>
                    )}
                    <div className={styles.row}>
                      <div className={styles.rowItem}>
                        <S mb={1}>
                          {t(
                            'web.admin.channel.visitor.passDetails.visitDetails.date'
                          )}
                        </S>
                        {isDateEditable && (
                          <DatePickerButton
                            wrapperClassName={styles.datePickerButtonWrapper}
                            buttonClassName={styles.datePickerButton}
                            value={new Date(startTime!)}
                            className={styles.datePicker}
                            onChange={() => {}}
                            hideLabel
                            onSubmit={updateDate}
                            minDate={new Date()}
                            maxDate={maxDate}
                            timeZone={timezone}
                          />
                        )}
                        {!isDateEditable && (
                          <Tooltip
                            TooltipComponent={t(
                              'web.admin.channel.visitor.passDetails.visitDetails.date.disabledTooltip'
                            )}
                            placement="bottom"
                          >
                            <Input
                              className={styles.disabledInput}
                              value={getPassDate(startTime)}
                              onChange={() => null}
                              showClear={false}
                              disabled
                              testId="disabledDateInput"
                            />
                          </Tooltip>
                        )}
                      </div>
                      <div className={styles.rowItem}>
                        <div className={styles.flexDisplay}>
                          <div className={styles.rowItem}>
                            <S mb={1}>
                              {t(
                                'web.admin.channel.visitor.passDetails.visitDetails.startTime'
                              )}
                            </S>
                            {isStartTimeEditable && (
                              <TimePicker
                                hideLabel
                                buttonClassName={styles.timePickerButton}
                                value={startTime}
                                onChange={updateStartTime}
                                showClear={false}
                                showIcon={false}
                                timeZone={timezone}
                                unit={TimeIntervals.QUARTER_HOUR}
                                testId="startTimeInput"
                              />
                            )}
                            {!isStartTimeEditable && (
                              <Tooltip
                                TooltipComponent={t(
                                  'web.admin.channel.visitor.passDetails.visitDetails.startTime.disabledTooltip'
                                )}
                                placement="bottom"
                              >
                                <Input
                                  className={styles.disabledInput}
                                  value={dateFormatter(
                                    startTime,
                                    SHORT_TIME_WITH_TZ,
                                    timezone
                                  )}
                                  onChange={() => null}
                                  showClear={false}
                                  disabled
                                  testId="disabledStartTimeInput"
                                />
                              </Tooltip>
                            )}
                          </div>
                          <div className={styles.rowItem}>
                            <S mb={1}>
                              {t(
                                'web.admin.channel.visitor.passDetails.visitDetails.endTime'
                              )}
                            </S>
                            {isEndTimeEditable && (
                              <TimePicker
                                hideLabel
                                buttonClassName={styles.timePickerButton}
                                value={endTime}
                                onChange={updateEndTime}
                                showClear={false}
                                showIcon={false}
                                unit={TimeIntervals.QUARTER_HOUR}
                                timeZone={timezone}
                                testId="endTimeInput"
                              />
                            )}
                            {!isEndTimeEditable && (
                              <Tooltip
                                TooltipComponent={t(
                                  'web.admin.channel.visitor.passDetails.visitDetails.endTime.disabledTooltip'
                                )}
                                placement="bottom"
                              >
                                <Input
                                  className={styles.disabledInput}
                                  value={dateFormatter(
                                    endTime,
                                    SHORT_TIME_WITH_TZ,
                                    timezone
                                  )}
                                  onChange={() => null}
                                  showClear={false}
                                  disabled
                                  testId="disabledEndTimeInput"
                                />
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      {visitorNotesEnabled && (
                        <div>
                          <div className={styles.row}>
                            <div className={styles.rowItem}>
                              <S mb={1}>
                                {t(
                                  'web.admin.channel.visitor.passDetails.visitDetails.noteToStaff'
                                )}
                              </S>
                              <TextArea
                                className={styles.textArea}
                                value={staffNote || ''}
                                onChange={updateStaffNote}
                                showClear={false}
                                maxLength={200}
                                showLengthIndicator
                                testId="staffNoteInput"
                              />
                            </div>
                          </div>
                          <div className={styles.row}>
                            <div className={styles.rowItem}>
                              <S mb={1}>
                                {t(
                                  'web.admin.channel.visitor.passDetails.visitDetails.noteToVisitor'
                                )}
                              </S>
                              <TextArea
                                className={styles.textArea}
                                value={visitorNote || ''}
                                onChange={updateVisitorNote}
                                showClear={false}
                                maxLength={200}
                                showLengthIndicator
                                testId="visitorNoteInput"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {multipleNotificationRecipientsEnabled && (
                        <div>
                          <div className={styles.row}>
                            <div className={styles.rowItem}>
                              <S mb={1}>
                                {t(
                                  'web.admin.channel.visitor.passDetails.visitDetails.additionalRecipients'
                                )}
                              </S>
                              <TagInput
                                tags={additionalRecipients || []}
                                setTags={updateAdditionalRecipients}
                                validateTag={validateEmail}
                                onValidationChange={
                                  handleAdditionalRecipientsErrorChange
                                }
                                placeholder={t(
                                  'shared.content.feature.visitorManagement.additional.recipients.notify.input.placeholder'
                                )}
                                maxTags={20}
                                maxTagInfo={t(
                                  'shared.content.feature.visitorManagement.additional.recipients.notify.input.maximum.email'
                                )}
                                errorText={t(
                                  'shared.content.feature.visitorManagement.additional.recipients.notify.input.error'
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {isEditMode && (
                <div className={styles.buttonContainer}>
                  <ComponentsButton
                    onClick={onEditSave}
                    testId="saveButton"
                    variant="activate-contained"
                    loading={isLoading}
                    disabled={isPristine}
                  >
                    {t('Save')}
                  </ComponentsButton>
                  <ComponentsButton onClick={onEditCancel} testId="cancelButton">
                    {t('Cancel')}
                  </ComponentsButton>
                </div>
              )}
              {showApprovalButtons && !isEditMode && (
                <div className={styles.approvalButtonContainer}>
                  <ApprovalActionButtons
                    onApprove={handleApprove}
                    onDeny={handleDeny}
                    isLoading={isApprovalLoading}
                  />
                </div>
              )}
            </div>
              </div>
              {shouldDisplayWatchlistMatchCards && hasWatchlistMatches && (
                <div className={styles.sidebar}>
                  <WatchlistMatchCards watchlists={watchlistMatches} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </AdminPage>
  );
}
